import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, RefreshCw, ShieldQuestion } from 'lucide-react';
import clsx from 'clsx';

import { adbApi } from '@/lib/api';
import { KvCard } from '@/components/KvCard';

type Mode = 'off' | 'opportunistic' | 'hostname';
interface PrivateDnsState {
  mode: Mode | 'unknown';
  hostname?: string;
}

const MODE_LABELS: Record<Mode, { title: string; hint: string }> = {
  off: {
    title: '关闭',
    hint: '走运营商默认 DNS（明文 UDP/TCP 53）。',
  },
  opportunistic: {
    title: '自动',
    hint: '可用时升级到 DNS-over-TLS，否则降级到明文。',
  },
  hostname: {
    title: '指定主机',
    hint: '强制 DNS-over-TLS 到指定 hostname；查询失败时不会降级。',
  },
};

const HOSTNAME_PRESETS = ['dns.alidns.com', 'dns.google', 'one.one.one.one', 'dns.quad9.net'];

/**
 * Bottom card. Reads `private_dns_mode` + `private_dns_specifier`, lets the
 * user pick one of three modes; in `hostname` mode the input below activates
 * with a small list of well-known TLS resolvers as `<datalist>` presets.
 *
 * On Android < 9 the mode comes back as `unknown` and we surface that with
 * a disabled state + explanatory copy instead of failing silently.
 */
export function DnsCard({ serial }: { serial?: string }) {
  const qc = useQueryClient();
  const enabled = !!serial;

  const dnsQ = useQuery({
    queryKey: ['net.privateDns.get', serial],
    queryFn: () => adbApi.run<PrivateDnsState>({ serial, name: 'net.privateDns.get' }),
    enabled,
    staleTime: 5_000,
  });

  const current = dnsQ.data?.result;
  const supported = current && current.mode !== 'unknown';
  const [mode, setMode] = useState<Mode>('off');
  const [hostname, setHostname] = useState('dns.alidns.com');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Hydrate the form with whatever the device reports the first time we get
  // a response. Subsequent device-side changes are reflected in `current`
  // but we don't clobber the user's in-progress edits.
  useEffect(() => {
    if (!current || current.mode === 'unknown') return;
    setMode(current.mode);
    if (current.hostname) setHostname(current.hostname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.mode]);

  const refresh = (): void => {
    qc.invalidateQueries({ queryKey: ['net.privateDns.get', serial] });
  };

  const submit = async (): Promise<void> => {
    if (!serial) return;
    if (mode === 'hostname' && !hostname.trim()) {
      setErr('hostname 不能为空');
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      const args: Record<string, unknown> =
        mode === 'hostname' ? { mode, hostname: hostname.trim() } : { mode };
      await adbApi.run({ serial, name: 'net.privateDns.set', args });
      refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <KvCard
      title="Private DNS · DNS-over-TLS"
      icon={<ShieldQuestion className="h-4 w-4 text-accent" />}
      action={
        <button
          className="btn-ghost"
          onClick={refresh}
          disabled={!enabled || dnsQ.isFetching}
          title="刷新"
        >
          <RefreshCw className={clsx('h-3.5 w-3.5', dnsQ.isFetching && 'animate-spin')} />
        </button>
      }
    >
      <div className="space-y-3">
        {dnsQ.error && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[12px]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            <div className="min-w-0">
              <div className="font-medium text-amber-600">无法读取 Private DNS 状态</div>
              <p className="mt-0.5 text-ink-muted break-words">
                {(dnsQ.error as Error).message}
              </p>
              {/^UNSUPPORTED_CMD/.test((dnsQ.error as Error).message) && (
                <p className="mt-0.5 text-ink-muted">
                  服务端没有 <code className="font-mono">net.privateDns.*</code>
                  。请重启 adh 服务（
                  <code className="font-mono">yarn adh:restart</code>
                  ，幂等替换）后再试。
                </p>
              )}
            </div>
          </div>
        )}
        <div className="text-[11px] text-ink-muted">
          当前：
          {!enabled ? (
            <span className="text-ink-dim"> 选择设备后查看</span>
          ) : !current ? (
            <span className="text-ink-dim">加载中...</span>
          ) : current.mode === 'unknown' ? (
            <span className="text-amber-500"> 未识别的模式（设备返回了非标准值）</span>
          ) : (
            <code className="ml-1 rounded bg-bg-soft px-1.5 py-0.5 font-mono text-[12px] text-ink">
              {current.mode}
              {current.mode === 'hostname' && current.hostname ? ` · ${current.hostname}` : ''}
            </code>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2">
          {(Object.keys(MODE_LABELS) as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              disabled={!enabled || !supported || busy}
              onClick={() => setMode(m)}
              className={clsx(
                'rounded-md border px-2 py-1.5 text-left transition disabled:opacity-50 disabled:cursor-not-allowed',
                mode === m
                  ? 'border-accent bg-accent-soft text-accent'
                  : 'border-line bg-bg-card hover:bg-bg-soft'
              )}
            >
              <div className="text-sm font-medium">{MODE_LABELS[m].title}</div>
              <div className="text-[10px] leading-tight text-ink-muted">{MODE_LABELS[m].hint}</div>
            </button>
          ))}
        </div>

        {mode === 'hostname' && (
          <div className="flex flex-wrap items-center gap-2">
            <input
              className="input min-w-[200px] flex-1"
              placeholder="dns.alidns.com"
              list="dns-hostname-presets"
              value={hostname}
              onChange={(e) => setHostname(e.target.value.trim())}
              disabled={!enabled || busy}
            />
            <datalist id="dns-hostname-presets">
              {HOSTNAME_PRESETS.map((h) => (
                <option key={h} value={h} />
              ))}
            </datalist>
          </div>
        )}

        <div className="flex items-center justify-end">
          <button
            className="btn-primary"
            disabled={!enabled || !supported || busy}
            onClick={submit}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            应用
          </button>
        </div>

        {err && <p className="text-[12px] text-rose-500">{err}</p>}
      </div>
    </KvCard>
  );
}
